import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, FileText, Globe, Truck, ScrollText, FileCheck, TriangleAlert, PackageCheck, Recycle, Inbox, ShieldCheck, Signature, Archive, Network, Landmark } from 'lucide-react';
import { CONTACT, PUBLIC_BOOKING_ENABLED } from '../config';
import { useLocalizedUrl } from '../utils/urlHelper';
import SeoMeta from '../components/SeoMeta';
import { Helmet } from 'react-helmet-async';
import ClientLogos from '../components/common/ClientLogos.jsx';

// Home B2B (nov-2026): pasada la obligación del DeCA, BlueCMR se presenta como
// proveedor de documentos digitales de transporte (nacional e internacional).
// Fuera el hero con la imagen generada, los dos bloques del DeCA, el bloque
// «Documentos de transporte» (sustituido por el listado) y el banner flotante
// de la guía 2026.

// Foto del hero, a pantalla completa con veladura a la izquierda (en móvil,
// veladura uniforme: el texto ocupa todo el ancho). position = encuadre del
// recorte (object-position). null = hero liso.
// Generada con ChatGPT (GPT Image 2) con marketing/prompts-imagenes/hero-autovia-olivar.txt;
// original en marketing/imagenes-ia/chatgpt/ (fuera de git).
const HERO_IMAGE = { src: '/images/home/hero-autovia-olivar.webp', position: '50% 50%' };

const NAVY = '#0b1d39';

const COPY = {
  es: {
    heroEyebrow: 'eCMR · Carta de porte · DeCA · ADR',
    heroTitle: 'Documentación de transporte digital, con plena validez legal',
    heroText: 'BlueCMR emite, firma y conserva los documentos de sus expediciones nacionales e internacionales en una sola plataforma, integrada con su TMS o ERP.',
    demo: 'Reserve su demostración',
    contact: 'Contacte con nosotros',
    platform: 'Conozca la plataforma',
    digitalEyebrow: 'Digitalización',
    digitalTitle: 'Del papel al documento digital, sin cambiar su operativa',
    digitalText: 'Los documentos de transporte se siguen imprimiendo, firmando a mano y archivando durante años. Con BlueCMR se emiten en formato digital, cada parte los firma desde su dispositivo y quedan conservados con todas sus evidencias, disponibles en una inspección y preparados para el intercambio de datos eFTI.',
    digitalPoints: [
      { title: 'Emisión', text: 'Desde su TMS o ERP por API, por fichero o desde la propia plataforma.' },
      { title: 'Firma', text: 'Firma electrónica avanzada de cargador, transportista y destinatario, sin instalar nada.' },
      { title: 'Conservación', text: 'PDF firmado en PAdES y evidencias de todo el ciclo de vida del documento.' },
    ],
    docsEyebrow: 'Documentos',
    docsTitle: 'Los documentos de cada expedición, en una sola plataforma',
    docsText: 'Transporte nacional e internacional, con firma electrónica avanzada y datos estructurados para eFTI.',
    docsLink: 'Ver el documento',
    docs: [
      { icon: Globe, scope: 'Internacional', code: 'eCMR', name: 'Carta de porte internacional electrónica.', basis: 'Convenio CMR y Protocolo eCMR', to: '/documentos/ecmr' },
      { icon: ScrollText, scope: 'España', code: 'Carta de porte', name: 'Contrato de transporte nacional, con los datos del documento de control.', basis: 'Ley 15/2009', to: '/documentos/carta-porte' },
      { icon: FileCheck, scope: 'España', code: 'DeCA', name: 'Documento electrónico de control administrativo, con una o varias entregas.', basis: 'Orden FOM/2861/2012', to: '/documentos/documento-control' },
      { icon: TriangleAlert, scope: 'Nacional e internacional', code: 'ADR', name: 'Documento de transporte de mercancías peligrosas, también dentro del eCMR.', basis: 'ADR, capítulo 5.4', to: '/documentos/transporte-adr' },
      { icon: FileText, scope: 'Francia', code: 'Lettre de voiture', name: 'Carta de porte nacional francesa, con el document de suivi.', basis: 'Arrêté du 9 novembre 1999' },
      { icon: PackageCheck, scope: 'España', code: 'Albarán de entrega', name: 'El albarán del cargador, con función de documento de control.', basis: 'Orden FOM/2861/2012' },
      { icon: Recycle, scope: 'España', code: 'DI de residuos', name: 'Documento de identificación de residuos, con función de documento de control.', basis: 'RD 553/2020' },
      { icon: Inbox, scope: 'Cadena de suministro', code: 'Documentos externos', name: 'Los CMR, albaranes, tiques de báscula o facturas de otras empresas se archivan por QR, enlace, PDF o correo, y la IA los clasifica y extrae sus datos.', basis: 'Archivo con lectura por IA' },
    ],
    clientsTitle: 'Algunas de las empresas que trabajan con BlueCMR',
    solEyebrow: 'Soluciones',
    solTitle: 'Para cada empresa de la cadena de transporte',
    solutions: [
      { key: 'cargadores', img: '/images/home/solucion-cargadores.webp', alt: 'Carga de palés en un semirremolque en el muelle de un almacén', title: 'Cargadores', text: 'Emita los documentos de cada expedición desde su ERP o desde la plataforma, compártalos con el transportista en un clic y siga cada envío hasta la entrega firmada.', link: 'BlueCMR para cargadores', to: '/cargadores' },
      { key: 'transportistas', img: '/images/home/solucion-transportistas.webp', alt: 'Conductor junto a la cabina de su camión consultando el móvil', title: 'Transportistas y flotas', text: 'Sus conductores reciben, muestran y firman los documentos desde el móvil, sin instalar nada, y la oficina tiene cada entrega firmada en cuanto se produce.', link: 'BlueCMR para transportistas', to: '/transportistas' },
      { key: 'software', img: '/images/home/solucion-software.webp', alt: 'Puesto de trabajo de un desarrollador con código y un panel logístico en pantalla', title: 'Proveedores de software', text: 'Integre eCMR, carta de porte y el resto de documentos en su TMS o ERP por API y webhooks, o en marca blanca, sin que sus clientes salgan de su plataforma.', link: 'BlueCMR para proveedores de software', to: '/proveedores-software' },
    ],
    trustEyebrow: 'Respaldo y garantías',
    trustTitle: 'Validez jurídica y seguridad, de la firma al archivo',
    trust: [
      { icon: ShieldCheck, title: 'Seguridad certificada (ENS)', text: 'BLUECMR, S.L.U. dispone de un sistema de gestión certificado de acuerdo al RD 311/2022 (ENS) por OCA CERT. Los datos se alojan en la Unión Europea.' },
      { icon: Signature, title: 'Firma electrónica avanzada', text: 'Conforme al artículo 26 del Reglamento eIDAS: identidad del firmante, documento y consentimiento vinculados de forma inequívoca.' },
      { icon: Archive, title: 'Evidencias que perduran', text: 'PDF firmado en PAdES con validación a largo plazo (LTV) y evidencias conservadas durante todo el ciclo de vida del documento.' },
      { icon: Network, title: 'Preparada para eFTI', text: 'Datos estructurados según el Reglamento (UE) 2020/1056 para el intercambio electrónico de información de transporte con las autoridades.' },
    ],
    trustLink: 'Cómo garantizamos la validez jurídica',
    resEyebrow: 'Recursos',
    resTitle: 'Normativa y transporte digital, explicados',
    resText: 'Artículos para entender qué exige cada documento y cómo prepararse para eFTI.',
    resLink: 'Ver todos los artículos',
    resources: [
      { icon: FileCheck, tag: 'DeCA', title: '¿Qué es el DeCA de transporte?', text: 'El documento de control digital, explicado: a quién obliga, qué requisitos exige y cómo cumplirlo.', to: '/articulos/que-es-el-deca' },
      { icon: Landmark, tag: 'Normativa', title: 'Requisitos técnicos del DeCA', text: 'Qué exige la Resolución de 5 de junio de 2026 al documento de control electrónico.', to: '/articulos/formato-pdfa1a-documento-control-digital-2026' },
      { icon: Network, tag: 'eFTI', title: 'Del PDF a los datos eFTI', text: 'Qué es eFTI y el calendario en España, del PDF obligatorio a los datos estructurados.', to: '/articulos/digitalizacion-documento-control-transporte-datos-efti' },
      { icon: FileText, tag: 'Documentos', title: 'Diferencias entre documento de control, carta de porte y CMR/eCMR', text: 'Diferencias, similitudes y posibilidades de integración entre los principales documentos de transporte.', to: '/articulos/diferencias-entre-dca-carta-de-porte-y-cmr-ecmr' },
    ],
    finalTitle: 'Digitalice la documentación de sus expediciones',
    finalPhone: 'o llámenos al',
  },
  en: {
    heroEyebrow: 'eCMR · Consignment notes · Control documents · ADR',
    heroTitle: 'Digital transport documents with full legal validity',
    heroText: 'BlueCMR issues, signs and preserves the documents of your domestic and international shipments on a single platform, integrated with your TMS or ERP.',
    demo: 'Book your demonstration',
    contact: 'Contact us',
    platform: 'Explore the platform',
    digitalEyebrow: 'Digitalisation',
    digitalTitle: 'From paper to digital documents, without changing how you operate',
    digitalText: 'Transport documents are still printed, signed by hand and archived for years. With BlueCMR they are issued digitally, each party signs from their own device, and they are preserved with all their evidence, ready for a roadside inspection and for eFTI data exchange.',
    digitalPoints: [
      { title: 'Issue', text: 'From your TMS or ERP via API, by file upload or from the platform itself.' },
      { title: 'Sign', text: 'Advanced electronic signature for shipper, carrier and consignee, with nothing to install.' },
      { title: 'Preserve', text: 'PAdES-signed PDF and evidence covering the whole document lifecycle.' },
    ],
    docsEyebrow: 'Documents',
    docsTitle: 'Every shipment document on a single platform',
    docsText: 'Domestic and international transport, with advanced electronic signature and data structured for eFTI.',
    docsLink: 'See the document',
    docs: [
      { icon: Globe, scope: 'International', code: 'eCMR', name: 'Electronic international consignment note.', basis: 'CMR Convention and eCMR Protocol', to: '/en/documents/ecmr' },
      { icon: ScrollText, scope: 'Spain', code: 'Carta de porte', name: 'Spanish domestic consignment note, including control document data.', basis: 'Law 15/2009', to: '/en/documents/road-waybill' },
      { icon: FileCheck, scope: 'Spain', code: 'DeCA', name: 'Electronic administrative control document, for one or several deliveries.', basis: 'Order FOM/2861/2012', to: '/en/documents/control-document' },
      { icon: TriangleAlert, scope: 'Domestic and international', code: 'ADR', name: 'Dangerous goods transport document, also within the eCMR.', basis: 'ADR, chapter 5.4', to: '/en/documents/adr-transport-document' },
      { icon: FileText, scope: 'France', code: 'Lettre de voiture', name: 'French domestic consignment note, with the document de suivi.', basis: 'Arrêté of 9 November 1999' },
      { icon: PackageCheck, scope: 'Spain', code: 'Delivery note', name: "The shipper's delivery note, acting as the control document.", basis: 'Order FOM/2861/2012' },
      { icon: Recycle, scope: 'Spain', code: 'Waste ID document', name: 'Waste identification document, acting as the control document.', basis: 'Royal Decree 553/2020' },
      { icon: Inbox, scope: 'Supply chain', code: 'External documents', name: 'CMRs, delivery notes, weighbridge tickets or invoices from other companies are archived by QR, link, PDF or email, and AI classifies them and extracts their data.', basis: 'Archive with AI reading' },
    ],
    clientsTitle: 'Some of the companies working with BlueCMR',
    solEyebrow: 'Solutions',
    solTitle: 'For every company in the transport chain',
    solutions: [
      { key: 'cargadores', img: '/images/home/solucion-cargadores.webp', alt: 'Pallets being loaded into a semi-trailer at a warehouse dock', title: 'Shippers', text: 'Issue the documents for every shipment from your ERP or the platform, share them with the carrier in one click and follow each consignment until it is delivered and signed.', link: 'BlueCMR for shippers', to: '/en/shippers' },
      { key: 'transportistas', img: '/images/home/solucion-transportistas.webp', alt: 'Truck driver next to the cab checking his phone', title: 'Carriers and fleets', text: 'Your drivers receive, show and sign documents from their phone with nothing to install, and the office sees every signed delivery as soon as it happens.', link: 'BlueCMR for carriers', to: '/en/carriers' },
      { key: 'software', img: '/images/home/solucion-software.webp', alt: 'Developer workstation with code and a logistics dashboard on screen', title: 'Software providers', text: 'Embed eCMR, consignment notes and the other documents in your TMS or ERP through our API and webhooks, or as a white label, without your customers leaving your platform.', link: 'BlueCMR for software providers', to: '/en/software-providers' },
    ],
    trustEyebrow: 'Assurance',
    trustTitle: 'Legal validity and security, from signature to archive',
    trust: [
      { icon: ShieldCheck, title: 'Certified security (ENS)', text: 'BLUECMR, S.L.U. holds a management system certified under Royal Decree 311/2022 (Spanish National Security Framework, ENS) by OCA CERT. Data is hosted in the European Union.' },
      { icon: Signature, title: 'Advanced electronic signature', text: 'Compliant with Article 26 of the eIDAS Regulation: signer identity, document and consent unequivocally linked.' },
      { icon: Archive, title: 'Long-lasting evidence', text: 'PAdES-signed PDF with long-term validation (LTV) and evidence preserved throughout the document lifecycle.' },
      { icon: Network, title: 'Ready for eFTI', text: 'Data structured under Regulation (EU) 2020/1056 for electronic freight transport information exchange with the authorities.' },
    ],
    trustLink: 'How we guarantee legal validity',
    finalTitle: 'Digitalise the documentation of your shipments',
    finalPhone: 'or call us on',
  },
};

// Botones de la home: rectos, sin radio (B2B).
const BTN_LIGHT = 'inline-flex items-center justify-center gap-2 bg-white font-semibold px-7 py-4 hover:bg-blue-50 transition-colors';
const BTN_GHOST = 'inline-flex items-center justify-center gap-2 border border-white/40 text-white font-semibold px-7 py-4 hover:bg-white/10 hover:border-white/70 transition-colors';

const Landing = () => {
  const { i18n } = useTranslation(['landing', 'common']);
  const isSpanish = i18n.language === 'es';
  const { getLocalizedUrl } = useLocalizedUrl();
  const c = COPY[isSpanish ? 'es' : 'en'];
  // Con las reservas apagadas (PUBLIC_BOOKING_ENABLED) la web solo ofrece
  // contactar (nosotros decidimos a quién invitamos).
  const primaryCta = PUBLIC_BOOKING_ENABLED
    ? { to: getLocalizedUrl('/contacto/demo'), label: c.demo }
    : { to: getLocalizedUrl('/contacto'), label: c.contact };
  return (
    <>
      {HERO_IMAGE && (
        <Helmet>
          <link rel="preload" href={HERO_IMAGE.src} as="image" fetchpriority="high" />
        </Helmet>
      )}
      <SeoMeta
        title="Documentos de transporte digitales: eCMR, carta de porte y DeCA | BlueCMR"
        description="Documentos de transporte digitales con validez legal: eCMR, carta de porte, DeCA y ADR, con firma electrónica avanzada e integración con su TMS o ERP."
        canonical="https://bluecmr.com/"
        type="website"
        keywords="deca transporte, deca, plataforma deca, documento electrónico de control administrativo, documento de control obligatorio digital, documento control administrativo, ecmr, carta de porte digital, cmr electronico, documentos transporte, efti, carta porte electronica, transporte mercancias digital, bluecmr, cmr digital"
        alternates={{
          es: 'https://bluecmr.com/',
          en: 'https://bluecmr.com/en',
          'x-default': 'https://bluecmr.com/'
        }}
        structuredData={[
          // Datos estructurados (4-nov-2026, también pensando en buscadores con IA):
          // organización completa (los asistentes la usan para describir la empresa),
          // la aplicación SIN precio (antes «price: 0» la hacía pasar por gratuita) y el
          // sitio, sin la búsqueda interna (/buscar no existe). En el idioma de la página.
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            '@id': 'https://www.bluecmr.com/#organization',
            name: 'BlueCMR',
            legalName: 'BLUECMR, S.L.U.',
            taxID: 'B26654517',
            url: 'https://www.bluecmr.com/',
            logo: 'https://www.bluecmr.com/og/logo-bluecmr.png',
            description: isSpanish
              ? 'Plataforma de documentos de transporte por carretera con validez legal: eCMR, carta de porte, DeCA y documento ADR, con firma electrónica avanzada, integración por API y conservación con evidencias.'
              : 'Platform for legally valid road transport documents: eCMR, consignment note, Spain’s DeCA control document and ADR transport document, with advanced electronic signature, API integration and preservation with evidence.',
            address: { '@type': 'PostalAddress', streetAddress: 'Calle Fueros de León 1', postalCode: '24402', addressLocality: 'Ponferrada', addressRegion: 'León', addressCountry: 'ES' },
            parentOrganization: { '@type': 'Organization', name: 'Cadenia' },
            contactPoint: [{
              '@type': 'ContactPoint',
              contactType: isSpanish ? 'ventas y soporte' : 'sales and support',
              telephone: '+34 987 191 362',
              email: 'contact@bluecmr.com',
              areaServed: 'EU',
              availableLanguage: ['es', 'en'],
            }],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'BlueCMR',
            url: isSpanish ? 'https://www.bluecmr.com/' : 'https://www.bluecmr.com/en',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web, iOS, Android',
            inLanguage: isSpanish ? 'es' : 'en',
            description: isSpanish
              ? 'Emisión, firma, envío, seguimiento y conservación de documentos de transporte digitales con validez legal, conectada con TMS y ERP.'
              : 'Issue, sign, send, track and preserve legally valid digital transport documents, connected to TMS and ERP systems.',
            featureList: isSpanish
              ? ['eCMR (Protocolo eCMR)', 'Carta de porte nacional (Ley 15/2009)', 'DeCA, documento de control (Orden FOM/2861/2012)', 'Documento de transporte ADR (ADR 5.4)', 'Firma electrónica avanzada (eIDAS) en el móvil, sin cuenta', 'PDF firmado en PAdES-LTV con evidencias', 'API REST y webhooks', 'Importación de ficheros en cualquier formato, también PDF', 'Archivo de documentos de otras empresas con lectura por IA', 'Datos estructurados según el estándar eFTI']
              : ['eCMR (eCMR Protocol)', 'Spanish domestic consignment note (Law 15/2009)', 'DeCA control document (Order FOM/2861/2012)', 'ADR transport document (ADR 5.4)', 'Advanced electronic signature (eIDAS) on any phone, no account needed', 'PAdES-LTV signed PDF with evidence', 'REST API and webhooks', 'File import in any format, PDF included', 'AI-read archive of documents from other companies', 'Data structured to the eFTI standard'],
            provider: { '@id': 'https://www.bluecmr.com/#organization' },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            url: 'https://www.bluecmr.com/',
            name: 'BlueCMR',
            inLanguage: isSpanish ? 'es-ES' : 'en',
            publisher: { '@id': 'https://www.bluecmr.com/#organization' },
          },
        ]}
      />
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ backgroundColor: NAVY }}>
        {HERO_IMAGE && (
          <>
            <img src={HERO_IMAGE.src} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: HERO_IMAGE.position || '50% 50%' }} loading="eager" fetchpriority="high" />
            <div className="absolute inset-0 md:hidden" style={{ backgroundColor: `${NAVY}cc` }} />
            <div className="absolute inset-0 hidden md:block" style={{ background: `linear-gradient(90deg, ${NAVY} 0%, ${NAVY}e6 38%, ${NAVY}80 62%, ${NAVY}26 100%)` }} />
          </>
        )}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="max-w-3xl">
            {/* Sin uppercase: respetaría mal «eCMR» y «DeCA» */}
            <p className="text-sm sm:text-[15px] font-semibold tracking-wide text-blue-300 mb-6">
              {c.heroEyebrow}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-semibold tracking-tight text-white leading-[1.08]">
              {c.heroTitle}
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-300 leading-relaxed max-w-2xl">
              {c.heroText}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to={primaryCta.to} className={BTN_LIGHT} style={{ color: NAVY }}>
                {primaryCta.label}
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to={getLocalizedUrl('/plataforma')} className={BTN_GHOST}>
                {c.platform}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Digitalización */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-700 mb-4">{c.digitalEyebrow}</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 leading-tight">{c.digitalTitle}</h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-lg text-slate-600 leading-relaxed">{c.digitalText}</p>
              <dl className="mt-10 grid sm:grid-cols-3 gap-8">
                {c.digitalPoints.map((point) => (
                  <div key={point.title} className="border-t-2 border-slate-900 pt-4">
                    <dt className="font-semibold text-slate-900">{point.title}</dt>
                    <dd className="mt-2 text-sm text-slate-600 leading-relaxed">{point.text}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
          {/* Clientes: al final de este bloque (propuesta del equipo, 4-nov) */}
          <ClientLogos title={c.clientsTitle} embedded />
        </div>
      </section>

      {/* Documentos que emite BlueCMR */}
      <section className="py-20 md:py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-700 mb-4">{c.docsEyebrow}</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 leading-tight">{c.docsTitle}</h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">{c.docsText}</p>
          </div>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-2">
            {c.docs.map((doc) => {
              const Icon = doc.icon;
              const body = (
                <>
                  <div className="flex items-center justify-between mb-5">
                    <Icon className="w-6 h-6 text-primary-700" strokeWidth={1.5} />
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{doc.scope}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900">{doc.code}</h3>
                  <p className="mt-2 text-[15px] text-slate-600 leading-relaxed">{doc.name}</p>
                  <p className="mt-4 text-sm text-slate-500">{doc.basis}</p>
                  {doc.to && (
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 group-hover:gap-2.5 transition-all">
                      {c.docsLink} <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </>
              );
              return (
                <li key={doc.code} className="border-t border-slate-300">
                  {doc.to ? (
                    <Link to={doc.to} className="group block pt-6 pb-8 h-full hover:bg-white/60 transition-colors">
                      {body}
                    </Link>
                  ) : (
                    <div className="pt-6 pb-8">{body}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Soluciones (antes «Para quién es BlueCMR»): miniaturas generadas con
          GPT Image 2 (marketing/prompts-imagenes/solucion-*.txt) */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-700 mb-4">{c.solEyebrow}</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 leading-tight">{c.solTitle}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-10 lg:gap-12">
            {c.solutions.map((sol) => (
              <article key={sol.key} className="group flex flex-col">
                <div className="aspect-[3/2] overflow-hidden bg-slate-100">
                  <img
                    src={sol.img}
                    alt={sol.alt}
                    width="768"
                    height="512"
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                </div>
                <h3 className="mt-6 text-xl font-semibold text-slate-900">{sol.title}</h3>
                <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{sol.text}</p>
                {sol.to && (
                  <Link to={sol.to} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:gap-2.5 transition-all">
                    {sol.link} <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Respaldo y garantías (rehecho 4-nov-2026, también en EN). ENS con la
          fórmula textual de OCA CERT (§4.5 de sus reglas de uso), sin categoría
          ni distintivo: el distintivo vive en /confianza/seguridad y en
          /cumplimiento-normativo, junto a la razón social. Datos «en la UE»
          (eu-south-2 y eu-west-3). eFTI: datos preparados, sin decir que seamos
          plataforma eFTI certificada. */}
      <section className="py-20 md:py-24 text-white" style={{ backgroundColor: NAVY }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-300 mb-4">{c.trustEyebrow}</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight leading-tight">{c.trustTitle}</h2>
          </div>
          <dl className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {c.trust.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="border-t border-white/20 pt-6">
                  <Icon className="w-6 h-6 text-blue-300 mb-4" strokeWidth={1.5} />
                  <dt className="font-semibold text-white">{item.title}</dt>
                  <dd className="mt-2 text-sm text-slate-300 leading-relaxed">{item.text}</dd>
                </div>
              );
            })}
          </dl>
          <Link to={getLocalizedUrl('/confianza/cumplimiento-normativo')} className="mt-12 inline-flex items-center gap-2 text-sm font-semibold text-blue-300 hover:text-white transition-colors">
            {c.trustLink} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Recursos (solo ES: los artículos solo existen en español). Fuera
          «¿Por qué BlueCMR?»: repetía digitalización y respaldo. */}
      {isSpanish && (
        <section className="py-20 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
              <div className="lg:col-span-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-700 mb-4">{c.resEyebrow}</p>
                <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 leading-tight">{c.resTitle}</h2>
                <p className="mt-4 text-lg text-slate-600 leading-relaxed">{c.resText}</p>
                <Link to="/articulos" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:gap-3 transition-all">
                  {c.resLink} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <ul className="lg:col-span-8 border-b border-slate-200">
                {c.resources.map((res) => {
                  const Icon = res.icon;
                  return (
                    <li key={res.to} className="border-t border-slate-200">
                      <Link to={res.to} className="group grid sm:grid-cols-12 gap-x-6 gap-y-2 py-6">
                        <div className="sm:col-span-3 flex items-center gap-3 self-start pt-0.5">
                          <Icon className="w-5 h-5 text-primary-700" strokeWidth={1.5} />
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{res.tag}</span>
                        </div>
                        <div className="sm:col-span-8">
                          <h3 className="font-semibold text-slate-900 group-hover:text-primary-700 transition-colors">{res.title}</h3>
                          <p className="mt-1 text-sm text-slate-600 leading-relaxed">{res.text}</p>
                        </div>
                        <ArrowRight className="hidden sm:block sm:col-span-1 justify-self-end self-center w-5 h-5 text-slate-400 group-hover:text-primary-700 group-hover:translate-x-1 transition" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Cierre */}
      <section className="py-24" style={{ backgroundColor: NAVY }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white leading-tight">
            {c.finalTitle}
          </h2>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-x-6 gap-y-4">
            <Link to={primaryCta.to} className={BTN_LIGHT} style={{ color: NAVY }}>
              {primaryCta.label}
              <ArrowRight className="w-5 h-5" />
            </Link>
            <span className="text-slate-300">
              {c.finalPhone}{' '}
              <a href={CONTACT.phoneHref} className="font-semibold text-white hover:underline whitespace-nowrap">{CONTACT.phoneDisplay}</a>
            </span>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Landing;

